import React, { useState } from 'react';
import './TaskCreator.css';

function TaskCreator({ onAddTask, onClose, suggestedStepGoal = 8000 }) {
  const [taskType, setTaskType] = useState('reminder');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState(2);
  const [recurring, setRecurring] = useState(null);
  
  // Exercise specific
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState(10);
  const [duration, setDuration] = useState(0);
  const [isFitGoal, setIsFitGoal] = useState(false);
  const [metricType, setMetricType] = useState('steps');
  const [targetValue, setTargetValue] = useState(suggestedStepGoal);
  const [autoComplete, setAutoComplete] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const taskData = {
      title,
      description,
      type: taskType,
      difficulty,
      recurring
    };

    if (isFitGoal) {
      taskData.goalSource = 'google-fit';
      taskData.metricType = metricType;
      taskData.targetValue = Number(targetValue) || 0;
      taskData.metricWindow = 'daily';
      taskData.autoComplete = autoComplete;
      taskData.recurring = recurring || 'daily';
    }

    if (taskType === 'exercise') {
      taskData.sets = sets;
      taskData.reps = reps;
      taskData.duration = duration;
    }

    onAddTask(taskData);
    
    // Reset form
    setTitle('');
    setDescription('');
    setDifficulty(2);
    setRecurring(null);
    setSets(3);
    setReps(10);
    setDuration(0);
    setIsFitGoal(false);
    setMetricType('steps');
    setTargetValue(suggestedStepGoal);
    setAutoComplete(true);
    
    if (onClose) onClose();
  };

  const difficultyLabels = {
    1: 'Easy',
    2: 'Medium',
    3: 'Hard',
    4: 'Very Hard',
    5: 'Legendary'
  };

  return (
    <div className="task-creator-overlay" onClick={onClose}>
      <div className="task-creator" onClick={(e) => e.stopPropagation()}>
        <div className="task-creator-header">
          <h2>Create New Task</h2>
          <button className="close-btn" onClick={onClose}>x</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Task Type */}
          <div className="form-group">
            <label>Task Type</label>
            <div className="task-type-buttons">
              <button
                type="button"
                className={`type-btn ${taskType === 'reminder' ? 'active' : ''}`}
                onClick={() => setTaskType('reminder')}
              >
                Reminder
              </button>
              <button
                type="button"
                className={`type-btn ${taskType === 'exercise' ? 'active' : ''}`}
                onClick={() => setTaskType('exercise')}
              >
                Exercise
              </button>
              <button
                type="button"
                className={`type-btn ${taskType === 'habit' ? 'active' : ''}`}
                onClick={() => setTaskType('habit')}
              >
                Habit
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What do you want to do?"
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add some details..."
              rows="3"
            />
          </div>

          {/* Exercise Specific Fields */}
          {taskType === 'exercise' && (
            <div className="exercise-fields">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="sets">Sets</label>
                  <input
                    id="sets"
                    type="number"
                    value={sets}
                    onChange={(e) => setSets(parseInt(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="reps">Reps</label>
                  <input
                    id="reps"
                    type="number"
                    value={reps}
                    onChange={(e) => setReps(parseInt(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="duration">Duration (sec)</label>
                  <input
                    id="duration"
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value))}
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Difficulty */}
          <div className="form-group">
            <label>Difficulty: {difficultyLabels[difficulty]}</label>
            <input
              type="range"
              min="1"
              max="5"
              value={difficulty}
              onChange={(e) => setDifficulty(parseInt(e.target.value))}
              className="difficulty-slider"
            />
            <div className="difficulty-labels">
              <span>Easy</span>
              <span>Hard</span>
            </div>
          </div>

          {/* Recurring */}
          <div className="form-group">
            <label>Repeat</label>
            <div className="recurring-buttons">
              <button
                type="button"
                className={`recurring-btn ${recurring === null ? 'active' : ''}`}
                onClick={() => setRecurring(null)}
              >
                Once
              </button>
              <button
                type="button"
                className={`recurring-btn ${recurring === 'daily' ? 'active' : ''}`}
                onClick={() => setRecurring('daily')}
              >
                Daily
              </button>
              <button
                type="button"
                className={`recurring-btn ${recurring === 'weekly' ? 'active' : ''}`}
                onClick={() => setRecurring('weekly')}
              >
                Weekly
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Google Fit Goal</label>
            <div className="recurring-buttons">
              <button
                type="button"
                className={`recurring-btn ${!isFitGoal ? 'active' : ''}`}
                onClick={() => setIsFitGoal(false)}
              >
                Off
              </button>
              <button
                type="button"
                className={`recurring-btn ${isFitGoal ? 'active' : ''}`}
                onClick={() => {
                  setIsFitGoal(true);
                  if (metricType === 'steps' && (!targetValue || targetValue < 1)) {
                    setTargetValue(suggestedStepGoal);
                  }
                }}
              >
                On
              </button>
            </div>
          </div>

          {isFitGoal && (
            <div className="exercise-fields">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fitMetricType">Metric</label>
                  <select
                    id="fitMetricType"
                    value={metricType}
                    onChange={(e) => {
                      const nextMetric = e.target.value;
                      setMetricType(nextMetric);
                      if (nextMetric === 'steps') {
                        setTargetValue(suggestedStepGoal);
                      } else if (nextMetric === 'heartRate' && (!targetValue || targetValue > 140 || targetValue < 40)) {
                        setTargetValue(100);
                      }
                    }}
                  >
                    <option value="steps">Daily Steps</option>
                    <option value="heartRate">Heart Rate (avg bpm)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="fitTargetValue">
                    Target {metricType === 'steps' ? '(steps)' : '(bpm)'}
                  </label>
                  <input
                    id="fitTargetValue"
                    type="number"
                    value={targetValue}
                    onChange={(e) => setTargetValue(parseInt(e.target.value || '0', 10))}
                    min={metricType === 'steps' ? '1000' : '40'}
                    step={metricType === 'steps' ? '500' : '1'}
                    required
                  />
                </div>
              </div>
              <div className="settings-toggle" style={{ marginTop: '0.5rem' }}>
                <span>Auto-complete when target is reached</span>
                <button
                  type="button"
                  className={`toggle-pill ${autoComplete ? 'on' : ''}`}
                  onClick={() => setAutoComplete(prev => !prev)}
                  aria-pressed={autoComplete}
                >
                  <span className="toggle-indicator" />
                </button>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              Create Task
            </button>
          </div>
        </form>

        {/* Snoopy decoration */}
        <div className="snoopy-corner">DJ</div>
      </div>
    </div>
  );
}

export default TaskCreator;
